import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { parseApiAsset, toBigInt } from '@morpho-org/dapp-core';
import { UNKNOWN_SYMBOL } from '@morpho-org/ui-v6/utils';
import { formatUnits } from 'viem';
import type { Warning } from '@/components/common/WarningIcon';
import type { UserLendPosition } from '@/hooks/positions/userLendPositionsQuery';
import { getParsedValue } from '@/helpers/formatters';
import { getNetRateOnMarket } from '@/helpers/getNativeApy';
import { getMarketRewards } from '@/helpers/rewards';
import { getMarketWarningMessage } from '@/helpers/warningMessages';
import { apiWarningLevelToEnum, isApiWarningLevel } from '@/helpers/warnings';
import { useBorrowPositionMarketIds } from '@/hooks/positions/useBorrowPositionMarketIds';
import { toBorrowPositionInputToken } from '@/hooks/positions/userBorrowPositionsQuery';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { isMarketLendFlowEnabled } from '@/lib/shared/utils/flags.constants';
export interface LendPositionsTableRowsProps {
  lendPositions: UserLendPosition[];
}
export const useLendPositionsTableRows = t0 => {
  const $ = _c(7);
  const {
    lendPositions
  } = t0;
  const flags = useFeatureFlags();
  let t1;
  if ($[0] !== flags) {
    t1 = isMarketLendFlowEnabled(flags);
    $[0] = flags;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const borrowPositionMarketIds = useBorrowPositionMarketIds(t1);
  let t2;
  if ($[2] !== borrowPositionMarketIds || $[3] !== lendPositions) {
    let t3;
    if ($[5] !== borrowPositionMarketIds) {
      t3 = lendPosition => {
        const {
          market
        } = lendPosition;
        const marketId = market.marketId as MarketId;
        const id = `${market.chainId}-${marketId}`;
        const loanAsset = parseApiAsset({
          ...toBorrowPositionInputToken(market.loanAsset),
          yield: market.loanAsset.yieldApy
        });
        const collateralAsset = parseApiAsset(market.collateralAsset ? toBorrowPositionInputToken(market.collateralAsset) : null);
        const warnings = [];
        for (const {
          type,
          level,
          content,
          badDebtUsd,
          badDebtAssets
        } of market.warnings) {
          if (!isApiWarningLevel(level)) {
            continue;
          }
          const message = getMarketWarningMessage({
            type,
            level,
            metadata: {
              content,
              badDebtUsd,
              badDebtAssets
            }
          }, loanAsset);
          if (message) {
            warnings.push({
              level: apiWarningLevelToEnum(level),
              message,
              warningType: type
            });
          }
        }
        const rewards = getMarketRewards({
          state: {
            rewards: market.supplyRewards.map(_temp)
          },
          loanAsset: toBorrowPositionInputToken(market.loanAsset)
        }, "supply");
        const avgSupplyApy = getParsedValue(market.apy.native, safeParseNumber);
        const avgNetSupplyApy = getNetRateOnMarket({
          rewards,
          nativeRate: avgSupplyApy
        });
        return {
          id,
          hasBorrowPosition: borrowPositionMarketIds?.has(id.toLowerCase()),
          market: {
            ...market,
            marketId,
            lltv: Number(formatUnits(BigInt(market.lltv), 18)),
            loanAsset: {
              ...market.loanAsset,
              symbol: market.loanAsset.symbol ?? UNKNOWN_SYMBOL
            },
            collateralAsset: market.collateralAsset && {
              ...market.collateralAsset,
              symbol: market.collateralAsset.symbol ?? UNKNOWN_SYMBOL
            }
          },
          collateral: {
            token: collateralAsset,
            warnings
          },
          lent: {
            amount: toBigInt(lendPosition.supplyAssets),
            value: lendPosition.supplyAssetsUsd,
            token: loanAsset
          },
          netApy: {
            avgNetSupplyApy,
            avgSupplyApy,
            rewards
          }
        };
      };
      $[5] = borrowPositionMarketIds;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = lendPositions.map(t3);
    $[2] = borrowPositionMarketIds;
    $[3] = lendPositions;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
function _temp(t0) {
  const {
    asset,
    supplyApr
  } = t0;
  return {
    supplyApr,
    asset: toBorrowPositionInputToken({
      ...asset,
      tags: null
    })
  };
}